import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Loader, AlertCircle, FileUp, TrendingUp,
  ArrowUpRight, ArrowDownRight, ChevronDown, RefreshCw,
} from 'lucide-react';
import { IS_TENANT } from '../tenant/config';

const API = '/.netlify/functions/wahr-sb';
async function call(action, body = {}) {
  const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...body }) });
  const j = await res.json().catch(() => ({ success: false, error: 'Bad response' }));
  if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
  return j;
}

const dim = 'var(--hp-dim)';
const text = 'var(--hp-text)';
const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' };
const n0 = (v) => Math.round(v).toLocaleString();
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Financial-year month order: Jul … Jun
const FY_MONTHS = [6, 7, 8, 9, 10, 11, 0, 1, 2, 3, 4, 5];

const DAY_MS = 86400000;
const dUTC = (iso) => new Date(iso + 'T00:00:00Z');
const isoOf = (d) => d.toISOString().slice(0, 10);
const addISO = (iso, n) => isoOf(new Date(dUTC(iso).getTime() + n * DAY_MS));
const sumDaily = (daily, start, end) => { let s = 0; for (const k in daily) { if (k >= start && k <= end) s += daily[k]; } return s; };
// FY is labelled by its ENDING year: Jul S–Jun S+1 → "FY (S+1)". Jul–Dec → year+1.
const fyOf = (iso) => { const d = dUTC(iso); return d.getUTCMonth() >= 6 ? d.getUTCFullYear() + 1 : d.getUTCFullYear(); };
const daysInMonth = (year, monthIdx) => new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();

export default function WahrComparisonPage() {
  const navigate = useNavigate();
  const [daily, setDaily] = useState({});        // iso → parcels (depot)
  const [periods, setPeriods] = useState([]);    // [{sourceFile,minDate,maxDate}] — real pay periods
  const [span, setSpan] = useState({ min: null, max: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const [midMode, setMidMode] = useState('period'); // 'period' | 'month'
  const [level, setLevel] = useState('year');      // 'year' | 'mid' | 'day'
  const [sel, setSel] = useState(null);            // drilled key: {monthIdx} or {period}

  const load = useCallback(async () => {
    try {
      const s = await call('getComparisonData'); // single parallel-paged call
      if (!s.rowCount) { setLoading(false); return; }
      setSpan({ min: s.minDate, max: s.maxDate });
      setDaily(s.daily || {});
      setPeriods(s.periods || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Pull the current in-progress pay periods from the live parcel_events feed
  // (same job as the 6am nightly auto-sync) so the newest FY column catches up to
  // today without waiting. Only touches dates after the last real WAHR file.
  const doSync = useCallback(async () => {
    setSyncing(true); setSyncMsg(''); setErr('');
    try {
      // Route through wahr-sb (a normal JSON function), NOT the scheduled wahr-sync
      // endpoint — Netlify discards a scheduled function's HTTP response body, which
      // surfaced here as "Bad response". Same shared runWahrSync job either way.
      const j = await call('runSync');
      setSyncMsg(j.synced ? `Synced ${(j.synced).toLocaleString()} rows from the live feed (through ${j.to}).` : 'Already up to date.');
      await load();
    } catch (e) { setErr(e.message); } finally { setSyncing(false); }
  }, [load]);

  // Financial years present, newest first (the column order).
  const fys = useMemo(() => {
    const set = new Set(); Object.keys(daily).forEach((iso) => set.add(fyOf(iso)));
    return [...set].sort((a, b) => b - a);
  }, [daily]);

  // Pre-aggregate parcels: total per FY, per FY×fy-month-position, per FY×month×day.
  const agg = useMemo(() => {
    const year = {}; const month = {}; const monthDay = {}; const days = {}; const monthDays = {};
    for (const [iso, val] of Object.entries(daily)) {
      const fy = fyOf(iso); const d = dUTC(iso); const cm = d.getUTCMonth(); const dom = d.getUTCDate();
      year[fy] = (year[fy] || 0) + val;
      const pos = FY_MONTHS.indexOf(cm);
      if (val > 0) {
        days[fy] = (days[fy] || 0) + 1; // distinct working (delivery) days in the FY
        (monthDays[fy] = monthDays[fy] || {})[pos] = (monthDays[fy][pos] || 0) + 1;
      }
      (month[fy] = month[fy] || {})[pos] = (month[fy][pos] || 0) + val;
      ((monthDay[fy] = monthDay[fy] || {})[pos] = monthDay[fy][pos] || {})[dom] = (monthDay[fy][pos][dom] || 0) + val;
    }
    return { year, month, monthDay, days, monthDays };
  }, [daily]);

  // Real AP pay periods = each imported file's date span. The period straddling
  // 30 Jun / 1 Jul is split into a June half (old FY) and July half (new FY).
  // Periods are numbered per FY in date order so P-rows align across FY columns
  // (P1 = the July half-period, P13/14 = the June half-period).
  const fyPeriods = useMemo(() => {
    const byFy = {};
    const add = (fy, seg) => { (byFy[fy] = byFy[fy] || []).push(seg); };
    for (const p of periods) {
      const a = p.minDate; const b = p.maxDate; if (!a || !b) continue;
      const fa = fyOf(a); const fb = fyOf(b);
      if (fa === fb) add(fa, { start: a, end: b });
      else { const cy = fb - 1; add(fa, { start: a, end: `${cy}-06-30`, half: true }); add(fb, { start: `${cy}-07-01`, end: b, half: true }); }
    }
    let maxLen = 0;
    for (const fy of Object.keys(byFy)) {
      byFy[fy].sort((x, y) => x.start.localeCompare(y.start));
      byFy[fy].forEach((s, i) => {
        s.idx = i + 1;
        s.total = sumDaily(daily, s.start, s.end);
        s.days = 0; for (const iso in daily) { if (iso >= s.start && iso <= s.end && daily[iso] > 0) s.days += 1; }
        // A segment bounded by the FY line (ends 30 Jun / starts 1 Jul) is a
        // half-period — whether from a straddle split or loaded directly.
        if (s.end.endsWith('-06-30') || s.start.endsWith('-07-01')) s.half = true;
      });
      maxLen = Math.max(maxLen, byFy[fy].length);
    }
    return { byFy, maxLen };
  }, [periods, daily]);
  const segAt = useCallback((fy, i) => (fyPeriods.byFy[fy] || []).find((s) => s.idx === i) || null, [fyPeriods]);

  // Year-to-date comparison for the "Full financial year" row. A partial/in-progress
  // FY must not be compared against a full prior FY (that reads as a huge false drop).
  // Instead compare it against the SAME slice of the prior year — from 1 Jul to the
  // same day-of-FY as the current year's last data. And if the prior year itself is
  // too sparse over that window (e.g. only a fortnight loaded), show no % at all.
  const yearCmp = useMemo(() => {
    const cmp = {};
    const maxD = {}; // last data date per FY
    for (const iso in daily) { if (daily[iso] > 0) { const fy = fyOf(iso); if (!maxD[fy] || iso > maxD[fy]) maxD[fy] = iso; } }
    for (let i = 0; i < fys.length; i += 1) {
      const fy = fys[i]; const fp = fys[i + 1]; if (fp == null) continue;
      const curMax = maxD[fy]; if (!curMax) continue;
      const curStart = `${fy - 1}-07-01`;
      const D = Math.round((dUTC(curMax) - dUTC(curStart)) / DAY_MS); // days into the FY
      const prevStart = `${fp - 1}-07-01`; const prevEnd = addISO(prevStart, D);
      let prevYTD = 0; let prevDays = 0;
      for (const iso in daily) { if (iso >= prevStart && iso <= prevEnd) { prevYTD += daily[iso]; if (daily[iso] > 0) prevDays += 1; } }
      const curDays = agg.days[fy] || 0;
      cmp[fy] = { cur: agg.year[fy] || 0, prev: prevYTD, show: prevYTD > 0 && prevDays >= curDays * 0.8, partial: D < 360 };
    }
    return cmp;
  }, [fys, agg, daily]);

  // Build the rows for the current level. Each row: {label, sub, vals:{fy:number}, onClick?}
  const view = useMemo(() => {
    if (!fys.length) return { title: '', rows: [] };
    const valYear = (fy) => agg.year[fy] || 0;

    if (level === 'year') {
      return {
        title: 'Financial year totals',
        crumbs: [],
        rows: [{ label: 'Full financial year', big: true, days: agg.days, cmp: yearCmp, vals: Object.fromEntries(fys.map((fy) => [fy, valYear(fy)])), onClick: () => setLevel('mid') }],
      };
    }

    if (level === 'mid' && midMode === 'month') {
      const rows = [{ label: 'Full year', total: true, days: agg.days, cmp: yearCmp, vals: Object.fromEntries(fys.map((fy) => [fy, valYear(fy)])) }];
      FY_MONTHS.forEach((cm, pos) => {
        rows.push({
          label: MONTH_LONG[cm], key: pos,
          vals: Object.fromEntries(fys.map((fy) => [fy, (agg.month[fy] && agg.month[fy][pos]) || 0])),
          daysByFy: Object.fromEntries(fys.map((fy) => [fy, (agg.monthDays[fy] && agg.monthDays[fy][pos]) || 0])),
          onClick: () => { setSel({ monthIdx: cm, pos }); setLevel('day'); },
        });
      });
      return { title: 'By month', crumbs: ['Year'], rows };
    }

    if (level === 'mid' && midMode === 'period') {
      const rows = [{ label: 'Full year', total: true, days: agg.days, cmp: yearCmp, vals: Object.fromEntries(fys.map((fy) => [fy, valYear(fy)])) }];
      for (let i = 1; i <= fyPeriods.maxLen; i += 1) {
        let seg = null; for (const fy of fys) { const s = segAt(fy, i); if (s) { seg = s; break; } }
        rows.push({
          label: `P${i}`, sub: seg ? periodLabel(seg) : '',
          vals: Object.fromEntries(fys.map((fy) => { const s = segAt(fy, i); return [fy, s ? s.total : 0]; })),
          daysByFy: Object.fromEntries(fys.map((fy) => { const s = segAt(fy, i); return [fy, s ? s.days : 0]; })),
          onClick: () => { setSel({ period: i }); setLevel('day'); },
        });
      }
      return { title: 'By pay period (AP cut-off dates)', crumbs: ['Year'], rows };
    }

    // day level
    if (level === 'day' && sel && sel.monthIdx != null) {
      const cm = sel.monthIdx; const pos = sel.pos;
      const rows = [{ label: `${MONTH_LONG[cm]} total`, total: true, vals: Object.fromEntries(fys.map((fy) => [fy, (agg.month[fy] && agg.month[fy][pos]) || 0])), daysByFy: Object.fromEntries(fys.map((fy) => [fy, (agg.monthDays[fy] && agg.monthDays[fy][pos]) || 0])) }];
      // Reference FY = newest one with data for this month; each prior FY aligns to the
      // SAME WEEKDAY 52 weeks (364 days) back, so a row compares Mon-to-Mon.
      const refFy = fys.find((fy) => agg.monthDay[fy] && agg.monthDay[fy][pos]) || fys[0];
      const refYear = cm >= 6 ? refFy - 1 : refFy;
      const nDays = daysInMonth(refYear, cm);
      for (let dom = 1; dom <= nDays; dom += 1) {
        const refISO = `${refYear}-${String(cm + 1).padStart(2, '0')}-${String(dom).padStart(2, '0')}`;
        const vals = {}; const datesByFy = {}; let any = false;
        fys.forEach((fy) => { const aligned = addISO(refISO, -(refFy - fy) * 364); vals[fy] = daily[aligned] || 0; datesByFy[fy] = aligned; if (vals[fy]) any = true; });
        if (any) rows.push({ label: fmtDow(refISO), vals, datesByFy });
      }
      return { title: `${MONTH_LONG[cm]} — by day (same weekday each year)`, crumbs: ['Year', midMode === 'month' ? 'Months' : 'Periods'], rows };
    }
    if (level === 'day' && sel && sel.period != null) {
      const i = sel.period;
      const rows = [{ label: `Period ${i} total`, total: true, vals: Object.fromEntries(fys.map((fy) => { const s = segAt(fy, i); return [fy, s ? s.total : 0]; })), daysByFy: Object.fromEntries(fys.map((fy) => { const s = segAt(fy, i); return [fy, s ? s.days : 0]; })) }];
      // Reference FY = newest with this period; prior FYs align to the SAME WEEKDAY
      // 52 weeks back (handles the July/June half-periods, which don't start on the
      // same weekday every year, unlike an index-based alignment).
      const refFy = fys.find((fy) => segAt(fy, i)) || fys[0];
      const refSeg = segAt(refFy, i);
      if (refSeg) {
        const len = Math.round((dUTC(refSeg.end) - dUTC(refSeg.start)) / DAY_MS) + 1;
        for (let k = 0; k < len; k += 1) {
          const refISO = addISO(refSeg.start, k);
          const vals = {}; const datesByFy = {}; let any = false;
          fys.forEach((fy) => { const aligned = addISO(refISO, -(refFy - fy) * 364); vals[fy] = daily[aligned] || 0; datesByFy[fy] = aligned; if (vals[fy]) any = true; });
          if (any) rows.push({ label: fmtDow(refISO), vals, datesByFy });
        }
      }
      return { title: `Pay period ${i} — by day (same weekday each year)`, crumbs: ['Year', 'Periods'], rows };
    }
    return { title: '', rows: [] };
  }, [level, midMode, sel, fys, agg, fyPeriods, segAt, daily, yearCmp]);

  const back = useCallback(() => {
    if (level === 'day') { setSel(null); setLevel('mid'); }
    else if (level === 'mid') setLevel('year');
  }, [level]);

  if (loading) return <div style={{ padding: 40, color: dim }}><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading WAHR history…</div>;

  return (
    <div style={{ padding: '28px 32px 48px', maxWidth: 1160, margin: '0 auto', color: text }}>
      <button onClick={() => navigate(-1)} style={linkBtn}><ChevronLeft size={16} /> Back</button>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 9, letterSpacing: '-0.01em' }}><TrendingUp size={24} /> WAHR Parcel Volumes</h1>
          <p style={{ color: dim, fontSize: 14, margin: '7px 0 0' }}>Parcel volume by financial year — drill from year to month/period to day.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {/* Live sync reads our parcel_events feed — a tenant deployment has none. */}
          {!IS_TENANT && (
          <button onClick={doSync} disabled={syncing} style={{ ...linkBtn, border: '1px solid var(--hp-border)', padding: '10px 16px', borderRadius: 'var(--radius)', color: 'var(--hp-accent)', opacity: syncing ? 0.6 : 1, cursor: syncing ? 'default' : 'pointer' }} title="Pull today's parcels from the live scan feed into the newest year">
            {syncing ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={15} />} {syncing ? 'Syncing…' : 'Sync live'}
          </button>
          )}
          <button onClick={() => navigate('/wahr-import')} style={{ ...linkBtn, border: '1px solid var(--hp-border)', padding: '10px 16px', borderRadius: 'var(--radius)', color: 'var(--hp-accent)' }}><FileUp size={15} /> Import</button>
        </div>
      </div>

      {syncMsg && <div style={{ ...card, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center', borderColor: 'rgba(16,185,129,0.4)', color: '#10b981', fontSize: 13, marginBottom: 16 }}><RefreshCw size={15} /> {syncMsg}</div>}
      {err && <div style={{ ...card, padding: 16, display: 'flex', gap: 10, alignItems: 'center', borderColor: 'rgba(239,68,68,0.4)', color: '#ef4444', fontSize: 13, marginBottom: 16 }}><AlertCircle size={16} /> {err}</div>}

      {!span.min ? (
        <div style={{ ...card, padding: 24, textAlign: 'center', color: dim }}>
          No WAHR history imported yet. <button onClick={() => navigate('/wahr-import')} style={{ color: 'var(--hp-accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Import files →</button>
        </div>
      ) : (
        <>
          {/* breadcrumb + controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
            {level !== 'year' && <button onClick={back} style={{ ...navBtn, width: 'auto', padding: '0 12px', gap: 6 }}><ChevronLeft size={16} /> Back</button>}
            <div style={{ fontSize: 15, fontWeight: 700 }}>{view.title}</div>
            {level === 'mid' && (
              <div style={{ display: 'inline-flex', border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden', marginLeft: 'auto' }}>
                {[['period', 'Period'], ['month', 'Month']].map(([v, l]) => (
                  <button key={v} onClick={() => setMidMode(v)} style={{ padding: '7px 15px', fontSize: 13.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: midMode === v ? 'var(--hp-accent)' : 'transparent', color: midMode === v ? '#fff' : text }}>{l}</button>
                ))}
              </div>
            )}
          </div>

          <div style={{ ...card, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 140 + fys.length * 150 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left', left: 0, position: 'sticky', background: 'var(--hp-card)', zIndex: 2, minWidth: 150 }}>{level === 'year' ? '' : 'Row'}</th>
                  {fys.map((fy) => (
                    <th key={fy} style={{ ...th, textAlign: 'right', minWidth: 140 }}>FY {fy}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row, ri) => {
                  const clickable = !!row.onClick;
                  return (
                    <tr key={row.label + ri}
                      onClick={row.onClick}
                      style={{
                        cursor: clickable ? 'pointer' : 'default',
                        background: row.total ? 'var(--hp-bg)' : ri % 2 ? 'transparent' : 'rgba(127,127,127,0.03)',
                        borderTop: row.total ? '1px solid var(--hp-border)' : 'none',
                      }}>
                      <td style={{ ...td, textAlign: 'left', left: 0, position: 'sticky', background: row.total ? 'var(--hp-bg)' : 'var(--hp-card)', zIndex: 1, fontWeight: row.total || row.big ? 700 : 600 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {clickable && <ChevronDown size={14} style={{ color: dim, transform: 'rotate(-90deg)' }} />}
                          <span>{row.label}{row.sub && <span style={{ color: dim, fontWeight: 400, fontSize: 12, marginLeft: 6 }}>{row.sub}</span>}</span>
                        </span>
                      </td>
                      {fys.map((fy, ci) => {
                        const v = row.vals[fy] || 0;
                        const prevFy = fys[ci + 1]; // the column to the RIGHT = older FY
                        const pv = prevFy != null ? (row.vals[prevFy] || 0) : null;
                        // Growth %: the Full-year row compares year-to-date (cmp);
                        // other rows compare like-for-like but only when this bucket's
                        // coverage is ≥80% of the prior year's (else the cell is partial).
                        let yoyEl = null;
                        if (row.cmp && row.cmp[fy]) {
                          const c = row.cmp[fy];
                          if (c.show) yoyEl = <YoY cur={c.cur} prev={c.prev} ytd={c.partial} />;
                        } else if (v > 0 && pv > 0) {
                          const cd = row.daysByFy ? (row.daysByFy[fy] || 0) : null;
                          const pd = row.daysByFy && prevFy != null ? (row.daysByFy[prevFy] || 0) : null;
                          if (!row.daysByFy || (pd > 0 && cd >= pd * 0.8)) yoyEl = <YoY cur={v} prev={pv} />;
                        }
                        return (
                          <td key={fy} style={{ ...td, textAlign: 'right' }}>
                            <div style={{ fontSize: row.big ? 24 : 15, fontWeight: row.big || row.total ? 800 : 600, lineHeight: 1.1 }}>{v ? n0(v) : <span style={{ color: dim }}>—</span>}</div>
                            {row.datesByFy && v > 0 && <div style={{ fontSize: 11, color: dim, marginTop: 2 }}>{fmtDayMon(row.datesByFy[fy])}</div>}
                            {yoyEl}
                            {row.days && v > 0 && <div style={{ fontSize: 11.5, color: dim, marginTop: 3 }}>{row.days[fy] || 0} working days</div>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 12, color: dim, marginTop: 14, lineHeight: 1.7 }}>
            Columns are financial years (1 Jul – 30 Jun), newest first; the small % under each figure is growth vs the year to its right.
            An in-progress year is compared year-to-date (marked <strong>YTD</strong>) — vs the same point last year, not the full year.
            Click a row to drill in; the day view lines up by weekday (Mon↔Mon, 52 weeks back). Data span {fmtNice(span.min)} – {fmtNice(span.max)}.
          </div>
        </>
      )}
    </div>
  );
}

// ── helpers ──
const th = { padding: '11px 16px', borderBottom: '1px solid var(--hp-border)', color: dim, fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '10px 16px', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };
const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: dim, cursor: 'pointer', padding: 0, marginBottom: 10, fontSize: 13 };
const navBtn = { height: 34, minWidth: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, color: text, cursor: 'pointer', fontSize: 13, fontWeight: 600 };

function YoY({ cur, prev, ytd }) {
  const pct = ((cur - prev) / prev) * 100;
  const up = pct > 0.05; const down = pct < -0.05;
  const color = up ? '#22c55e' : down ? '#ef4444' : dim;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : null;
  return (
    <div style={{ fontSize: 11.5, fontWeight: 700, color, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, marginTop: 2 }}>
      {Icon && <Icon size={12} />}{pct > 0 ? '+' : ''}{(Math.round(pct * 10) / 10).toFixed(1)}%
      {ytd && <span style={{ fontSize: 9, color: dim, marginLeft: 3, fontWeight: 600 }} title="Year-to-date: vs the same point last year">YTD</span>}
    </div>
  );
}

const dUTC2 = (iso) => new Date(iso + 'T00:00:00Z');
function fmtDow(iso) { const d = dUTC2(iso); return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`; }
function fmtNice(iso) { const d = dUTC2(iso); return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`; }
function fmtDayMon(iso) { const d = dUTC2(iso); return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`; }
// Label a real pay-period segment by its date range, marking the June/July halves.
function periodLabel(seg) {
  const s = dUTC2(seg.start); const e = dUTC2(seg.end);
  const range = `${s.getUTCDate()} ${MONTHS[s.getUTCMonth()]} – ${e.getUTCDate()} ${MONTHS[e.getUTCMonth()]}`;
  return seg.half ? `${range} · ½` : range;
}
